import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CANDIDATE_ENDPOINTS } from '../../constants/apiEndpoints';

function EvaluationEntry() {
  const navigate = useNavigate();
  const { evaluation_id } = useParams();
  
  // State for evaluation template data
  const [templateData, setTemplateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    fname: '',
    lname: '',
    email: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Fetch evaluation template data on mount
  useEffect(() => {
    const fetchTemplateData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(
          `${CANDIDATE_ENDPOINTS.GET_EVALUATION_TEMPLATE}?evaluation_id=${evaluation_id}`
        );
        
        if (!response.ok) {
          throw new Error('Failed to fetch evaluation data');
        }
        
        const data = await response.json();
        
        // Handle potential array response (similar to WelcomeScreen)
        const templateInfo = Array.isArray(data) ? data[0] : data;
        
        if (!templateInfo) {
          throw new Error('Invalid evaluation data received');
        }
        
        // Validate that evaluation_id is returned - if not, evaluation doesn't exist
        if (!templateInfo.evaluation_id) {
          throw new Error('Evaluation does not exist');
        }
        
        setTemplateData(templateInfo);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching evaluation template:', err);
        setError(err.message || 'Failed to load evaluation data');
        setLoading(false);
      }
    };

    if (evaluation_id) {
      fetchTemplateData();
    } else {
      setError('No evaluation ID provided');
      setLoading(false);
    }
  }, [evaluation_id]);

  // Update page title when template data loads
  useEffect(() => {
    if (templateData) {
      const title = templateData.job_name || templateData.title || 'Evaluation';
      const company = templateData.company_name || '';
      document.title = company ? `${title} - ${company}` : title;
    }
  }, [templateData]);

  // Validate email format
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle form field changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
    
    // Clear submit error when user makes changes
    if (submitError) {
      setSubmitError(null);
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!formData.fname.trim()) {
      errors.fname = 'First name is required';
    }
    
    if (!formData.lname.trim()) {
      errors.lname = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setSubmitting(true);
      setSubmitError(null);
      
      const response = await fetch(CANDIDATE_ENDPOINTS.CREATE_CANDIDATE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          evaluation_id: templateData.evaluation_id,
          job_id: templateData.job_id,
          uid: templateData.uid,
          fname: formData.fname.trim(),
          lname: formData.lname.trim(),
          email: formData.email.trim()
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to create candidate');
      }
      
      const result = await response.json();
      
      console.log('Create candidate response:', result);
      
      // Expect the response to contain the eval_url
      if (result.eval_url) {
        // Navigate to the evaluation URL
        window.location.href = result.eval_url;
      } else {
        throw new Error('No evaluation URL received');
      }
    } catch (err) {
      console.error('Error creating candidate:', err);
      setSubmitError(err.message || 'Failed to start evaluation. Please try again.');
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-gray-200">
          <div className="inline-block h-20 w-20 animate-spin rounded-full border-4 border-solid border-gray-500 border-r-transparent mb-6"></div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">Loading Evaluation</h2>
          <p className="text-gray-600 max-w-md">Please wait while we prepare your evaluation...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="text-center max-w-md p-8 bg-white rounded-xl shadow-lg border border-red-100">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-50 rounded-full mb-6">
            <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error Loading Evaluation</h2>
          <p className="text-gray-700 mb-6">
            {error || "We couldn't load this evaluation. Please check the link and try again."}
          </p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-medium rounded-lg hover:from-red-600 hover:to-red-700 transition-colors duration-200 shadow-md"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Render logo - show company logo if exists, otherwise company name, otherwise nothing
  const renderLogo = () => {
    if (templateData.company_logo) {
      return (
        <img 
          src={templateData.company_logo} 
          alt={`${templateData.company_name || 'Company'} logo`} 
          className="max-w-full max-h-32 object-contain p-2" 
        />
      );
    } else if (templateData.company_name) {
      return (
        <h2 className="text-2xl font-bold text-center whitespace-nowrap">
          {templateData.company_name}
        </h2>
      );
    }
    // If neither logo nor name exists, show nothing
    return null;
  };

  return (
    <div className="flex flex-col min-h-screen p-0 pt-20 pb-16 w-full">
      <div className="flex-1 flex justify-center px-4">
        <div className="w-full max-w-xl bg-white rounded-3xl shadow-md p-12">
          {/* Company Logo */}
          <div className="w-full flex items-center justify-center mb-10" style={{ minHeight: '128px' }}>
            {renderLogo()}
          </div>
          
          {/* Welcome Message */}
          <div className="text-center mb-10">
            <h1 className="text-4xl font-bold mb-3">
              Welcome!
            </h1>
            <p className="text-xl text-gray-700 max-w-xl leading-relaxed">
              {templateData.job_name ? (
                <>Please register below to access your evaluation for the <span className="font-semibold">{templateData.job_name}</span> role{templateData.company_name ? <> at <span className="font-semibold">{templateData.company_name}</span></> : null}.</>
              ) : (
                <>Register below to access your evaluation.</>
              )}
            </p>
          </div>
          
          {/* Registration Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* First Name & Last Name - 2 Column Layout */}
            <div className="flex gap-4 mb-6">
              {/* First Name */}
              <div className="flex-1">
                <label className="block mb-2">
                  <span className="font-semibold">* First Name</span>
                </label>
                <input
                  type="text"
                  id="fname"
                  name="fname"
                  value={formData.fname}
                  onChange={handleInputChange}
                  placeholder="Roberta"
                  className={`w-full p-3 border ${formErrors.fname ? 'border-red-500' : 'border-gray-300'} rounded`}
                  disabled={submitting}
                />
                {formErrors.fname && (
                  <div className="bg-red-50 border-l-4 border-red-500 p-3 mt-1 rounded">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-red-700">{formErrors.fname}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Last Name */}
              <div className="flex-1">
                <label className="block mb-2">
                  <span className="font-semibold">* Last Name</span>
                </label>
                <input
                  type="text"
                  id="lname"
                  name="lname"
                  value={formData.lname}
                  onChange={handleInputChange}
                  placeholder="Bryson"
                  className={`w-full p-3 border ${formErrors.lname ? 'border-red-500' : 'border-gray-300'} rounded`}
                  disabled={submitting}
                />
                {formErrors.lname && (
                  <div className="bg-red-50 border-l-4 border-red-500 p-3 mt-1 rounded">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-red-700">{formErrors.lname}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Email */}
            <div className="mb-6">
              <label className="block mb-2">
                <span className="font-semibold">* E-mail Address</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="email@address.com"
                className={`w-full p-3 border ${formErrors.email ? 'border-red-500' : 'border-gray-300'} rounded`}
                disabled={submitting}
              />
              {formErrors.email && (
                <div className="bg-red-50 border-l-4 border-red-500 p-3 mt-1 rounded">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{formErrors.email}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Submit Error */}
            {submitError && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{submitError}</p>
              </div>
            )}
            
            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              className={`w-full py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xl font-bold rounded-lg hover:from-blue-600 hover:to-blue-700 transform hover:scale-105 transition-all duration-200 shadow-lg flex items-center justify-center ${
                submitting ? 'opacity-70 cursor-not-allowed hover:scale-100' : ''
              }`}
            >
              {submitting ? (
                <>
                  <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-solid border-white border-r-transparent mr-3"></div>
                  Please wait...
                </>
              ) : (
                <>
                  Continue
                  <svg className="w-6 h-6 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
                  </svg>
                </>
              )}
            </button>
          </form>
          
          {/* Footer */}
          <div className="border-t border-gray-200 pt-6 mt-10">
            <div className="text-xs text-gray-500 text-center">
              Powered by tendent.ai
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EvaluationEntry;
